/* eslint-disable no-param-reassign */
import {Box, FormControl, Stack, Typography, useTheme} from '@mui/material';
import {AutoSolEnum} from 'main/analytics/gaTagsEnum';
import {
	makeDeleteRequestFile,
	makePostRequestUploadFile,
} from 'main/factories/usecases/private/authorizations';
import {IconIllustration} from 'presentation/components';
import {ALLOWED_EXTENSIONS} from 'presentation/components/UploadFiles/constants';
import {useUploadFile} from 'presentation/components/UploadFiles/hooks/useUploadFile';
import {useUploadedFilesStore} from 'presentation/components/UploadFiles/store/useUploadedFilesStore';
import {SendingFilesSchema} from 'presentation/components/UploadFiles/validations';
import {useGetAuthDetail} from 'presentation/pages/private/authorizations/queries/useGetAuthDetail';
import {translations} from 'presentation/translations-new';
import React, {Fragment, useState} from 'react';
import {UploadFilesProps} from '../types';
import {FileUpload} from './FileUpload';
import {Container, FileInput, Label} from './styles';

const {advice} = translations['pt-br'].others.uploadFiles;
const {addAttachment} = translations['pt-br'].privateFlow.requestAuthorizations.attachments;

export function UploadFiles({nuControle}: UploadFilesProps) {
	const theme = useTheme();

	const {uploadedFiles, handleUpload, IS_UPLOADING} = useUploadFile({
		postFactory: makePostRequestUploadFile,
		deleteFactory: attachmentId =>
			makeDeleteRequestFile({nuArquivo: attachmentId, nuControle: nuControle.toString()}),
		nuControle: nuControle.toString(),
		showNotification: true,
	});

	const {refetch: refresh} = useGetAuthDetail(nuControle);

	const resetUploadedFilesState = useUploadedFilesStore(state => state.resetUploadedFiles);

	const [fileState, setFileState] = useState<File>();

	const [errorState, setErrorState] = useState<string | null>(null);
	const [isOnDropState, setIsOnDropState] = useState(false);
	const allowedExtensions = ALLOWED_EXTENSIONS.join(', ');

	const handleRemoveFile = () => handleChangeFile(undefined);

	const handleChangeFile = (file: File | undefined) => setFileState(file);

	function handleOnChange(e: React.ChangeEvent<HTMLInputElement>) {
		setErrorState(null);
		const {files} = e.target;

		if (!files) {
			return;
		}

		const result = SendingFilesSchema.safeParse(files[0]);

		if (!result.success) {
			setErrorState(result.error.issues[0].message);
			return;
		}

		setFileState(files[0]);
		e.target.value = '';
		setIsOnDropState(false);
	}

	const handleOnDrop = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setErrorState(null);
		const {files} = event.dataTransfer;

		if (!files) {
			return;
		}

		const result = SendingFilesSchema.safeParse(files[0]);

		if (!result.success) {
			setErrorState(result.error.issues[0].message);
			return;
		}

		setFileState(files[0]);
		setIsOnDropState(false);
	};

	const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsOnDropState(true);
		setErrorState(null);
	};

	const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsOnDropState(false);
		setErrorState(null);
	};

	const handleSendFile = () => {
		setErrorState(null);
		if (!fileState) {
			return;
		}
		handleUpload([fileState]);
		setTimeout(() => {
			if (!uploadedFiles[0]?.error) {
				refresh();
				resetUploadedFilesState();
				return;
			}
			setErrorState('Não foi possível fazer o upload do arquivo, tente novamente');
		}, 1500);
	};

	return (
		<Fragment>
			{fileState ? (
				<Stack
					border={`1px dashed ${theme.colors.gray['40Percent']}`}
					borderRadius={1}
					direction={'column'}
					gap={2}>
					<FileUpload
						size={fileState.size}
						title={fileState.name}
						loading={IS_UPLOADING}
						progress={uploadedFiles[0]?.progress}
						error={uploadedFiles[0]?.error}
						removeFile={handleRemoveFile}
						sendFile={handleSendFile}
						removeButtonDataTagId={AutoSolEnum.CLICK_REMOVER_ARQUIVO_ANEXO_AUTOSOL}
					/>
				</Stack>
			) : (
				<Container
					$isError={!!errorState}
					justifyContent="center"
					alignItems="center"
					sx={{bgcolor: errorState ? 'error' : 'white'}}
					onDrop={handleOnDrop}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					$isOnDrop={isOnDropState}
					spacing={1}>
					<IconIllustration
						customIconName="upload-file-filled"
						variant={errorState ? 'error' : 'primary'}
						size="custom"
					/>
					<FormControl data-tag-id={AutoSolEnum.AS_CLICK_ADICIONAR_ANEXO}>
						<Label htmlFor="file" as="label">
							{addAttachment}
						</Label>

						<FileInput
							id="file"
							type="file"
							accept={allowedExtensions}
							onChange={e => handleOnChange(e)}
						/>
					</FormControl>

					<Box sx={{textAlign: 'center'}}>
						{errorState ? (
							<Typography variant="body2" color="error">
								{errorState}
							</Typography>
						) : null}

						<Typography
							variant="body2"
							textAlign="center"
							color={errorState ? 'error' : 'text.secondary'}>
							{advice}
						</Typography>
					</Box>
				</Container>
			)}
		</Fragment>
	);
}
